// The classic login's stand-in (mock only, decided 2026-10-29).
//
// In production "Log in" goes to the classic login (LoginProject) with page=submission-{step},
// and the login hands the actor back to /submission/flow/continue signed in (docs/28). The
// mock has no login, so an existing member's journey stopped at step 1. This page takes the
// same parameters, signs in a demo account of the kind picked here (an existing account, or an
// existing PRO one), and goes to the continue page by a full page load, as the real login does:
// the store is empty there, so the flow rebuilds itself from the session.
//
// Watermarked, and it refuses to render outside the mock API.

import React, { useState } from "react";
import styled from "styled-components";
import i18next from "i18next";
import qs from "query-string";
import SubmissionLayout from "../common/SubmissionLayout";
import { getSubmissionFlowApi, getMockScenario, isMockApi } from "../../../shared/api/submission";
import { MOCK_EMAIL_CODE } from "../../../shared/api/submission/mockSession";
import { setSubmissionAuth } from "../../../helpers/submissionAuth";
import { resetMockBackend } from "../../../shared/api/submission/SubmissionFlowApi.mock";
import { membershipPrimary } from "../common/membershipButton";

interface Props {
  history: any;
  location: any;
}

type Kind = "existingUser" | "existingPro";

const KINDS: { value: Kind; label: string }[] = [
  { value: "existingUser", label: "Existing account" },
  { value: "existingPro", label: "Existing PRO account" },
];

// What the login forwards (LoginProject's submissionFlow.js): the flow's own params and the step.
const FORWARDED = ["bd", "roleid", "src", "page"];

const MockLoginPage: React.FC<Props> = ({ history, location }) => {
  const query: any = qs.parse(location.search) || {};
  const [login, setLogin] = useState<string>(typeof query.login === "string" ? query.login : "");
  const [password, setPassword] = useState("");
  const [kind, setKind] = useState<Kind>(getMockScenario() === "existingPro" ? "existingPro" : "existingUser");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!isMockApi()) {
    history.replace("/submission/casting-calls");
    return null;
  }

  const onLogIn = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      // A fresh demo account of the kind picked, made through the public seam as the seed page
      // does. The scenario sticks for the tab (sfMockScenario), so every later answer is that
      // account's: an existing user's library and job alerts, a PRO plan.
      window.sessionStorage.setItem("sfMockScenario", kind);
      resetMockBackend();
      const api = getSubmissionFlowApi();
      const geo = await api.getRequesterLocation();
      const start = await api.startSignup({
        firstName: "Paola",
        lastName: "Hernandez",
        email: login.indexOf("@") > 0 ? login : "paola.demo@theworkbook.com",
        password: password || "abc123",
        recaptchaToken: "mock-login",
        breakdownId: typeof query.bd === "string" ? query.bd : undefined,
        roleId: typeof query.roleid === "string" ? query.roleid : undefined,
        geoCountryCode: geo.countryCode || undefined,
        geoRegionCode: geo.regionCode,
        geoCityId: geo.cityId,
      });
      const verified = await api.verifyEmailCode(start.sessionId, MOCK_EMAIL_CODE);
      if (verified.jwt && verified.clientId) setSubmissionAuth({ clientId: verified.clientId, jwt: verified.jwt });
      const back: any = {};
      FORWARDED.forEach((key) => {
        if (query[key]) back[key] = query[key];
      });
      window.location.assign(`/submission/flow/continue?${qs.stringify(back)}`);
    } catch (e) {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <SubmissionLayout>
      <Watermark>{i18next.t("MOCK LOGIN — the classic login in production")}</Watermark>
      <Panel>
        <PanelTitle>{i18next.t("Log in to theWorkbook")}</PanelTitle>
        <PanelText>{i18next.t("Then back to your submission.")}</PanelText>
        <Field
          aria-label={i18next.t("Email or Workbook ID")}
          placeholder={i18next.t("Email or Workbook ID")}
          autoComplete="username"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
        />
        <Field
          type="password"
          aria-label={i18next.t("Password")}
          placeholder={i18next.t("Password")}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <KindLabel htmlFor="mock-login-kind">{i18next.t("Demo account")}</KindLabel>
        <Select id="mock-login-kind" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {i18next.t(k.label)}
            </option>
          ))}
        </Select>
        {failed && <Failed role="alert">{i18next.t("Something went wrong. Please try again.")}</Failed>}
        <PrimaryButton type="button" onClick={onLogIn} disabled={busy}>
          {busy ? i18next.t("One moment…") : i18next.t("Log in")}
        </PrimaryButton>
      </Panel>
    </SubmissionLayout>
  );
};

const Watermark = styled.div`
  margin: 12px 0;
  padding: 8px 10px;
  border: 1px dashed #c7053f;
  border-radius: 6px;
  color: #c7053f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: center;
`;

const Panel = styled.div`
  border: 1px solid #e3e8ee;
  border-radius: 10px;
  padding: 16px;
`;

const PanelTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 4px;
`;

const PanelText = styled.div`
  font-size: 13px;
  color: #53627c;
  margin-bottom: 14px;
`;

const Field = styled.input`
  width: 100%;
  height: 46px;
  margin-bottom: 10px;
  padding: 0 12px;
  border: 1px solid #c9d3e0;
  border-radius: 5px;
  font-size: 15px;
  color: #122640;
  box-sizing: border-box;
  outline: 0;

  &:focus {
    border-color: #0f9bf0;
  }
`;

const KindLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #53627c;
  margin: 6px 0 4px;
`;

const Select = styled.select`
  width: 100%;
  height: 46px;
  margin-bottom: 14px;
  padding: 0 10px;
  border: 1px solid #c9d3e0;
  border-radius: 5px;
  background: #ffffff;
  font-size: 15px;
  color: #122640;
`;

const Failed = styled.div`
  font-size: 13px;
  color: #c7053f;
  margin-bottom: 10px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

export default MockLoginPage;
